import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { LEGACY_TOKEN_KEY, tokenKey, getStoredToken, setStoredToken, clearStoredToken } from "../utils/authStorage";

const API = process.env.REACT_APP_API_URL || "https://g6fw4rjkvb.execute-api.eu-central-1.amazonaws.com/prototype";
const AuthContext = createContext(null);

// Every role this context manages gets its own independent session slot —
// see utils/authStorage.js for why. (road_rep and admin already had their
// own separate ad-hoc localStorage keys before this and aren't routed
// through AuthContext at all, so they're unaffected.)
const ROLES = ["winery_owner", "traveler"];

const emptySession = { user: null, winery: null, loading: true };

async function fetchMe(token) {
  const r = await fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
  return r.json();
}

export function AuthProvider({ children }) {
  const [sessions, setSessions] = useState(() =>
    Object.fromEntries(ROLES.map(r => [r, { ...emptySession }]))
  );

  const patchSession = useCallback((role, patch) => {
    setSessions(s => ({ ...s, [role]: { ...s[role], ...patch } }));
  }, []);

  useEffect(() => {
    (async () => {
      // One-time migration: this used to be a single shared "wr_token" key
      // for whichever role logged in most recently. Adopt it into the
      // right role-scoped slot (found by asking the API who it belongs to)
      // so existing logged-in sessions don't just vanish on this deploy.
      const legacy = localStorage.getItem(LEGACY_TOKEN_KEY);
      if (legacy) {
        try {
          const data = await fetchMe(legacy);
          if (data.user && ROLES.includes(data.user.role) && !getStoredToken(data.user.role)) {
            setStoredToken(data.user.role, legacy);
          }
        } catch { /* ignore — legacy token unusable, just drop it below */ }
        localStorage.removeItem(LEGACY_TOKEN_KEY);
      }

      ROLES.forEach(role => {
        const token = getStoredToken(role);
        if (!token) { patchSession(role, { loading: false }); return; }
        fetchMe(token)
          .then(data => {
            if (data.user && data.user.role === role) {
              patchSession(role, { user: data.user, winery: data.winery || null, loading: false });
            } else {
              clearStoredToken(role);
              patchSession(role, { loading: false });
            }
          })
          .catch(() => { clearStoredToken(role); patchSession(role, { loading: false }); });
      });
    })();
  }, [patchSession]);

  // role param is the role being logged into; defaults to winery_owner to
  // preserve the existing /manage/login call site (login(email, password)).
  // Passing role=null skips the "did I get the role I asked for" check.
  const login = useCallback(async (email, password, role = "winery_owner") => {
    const r = await fetch(`${API}/auth/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, password, role: role || undefined }) });
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || "Login failed");
    if (role && data.user?.role !== role) throw new Error(`This login is for ${role === "winery_owner" ? "winery managers" : role + " accounts"} only.`);
    const actualRole = data.user.role;
    setStoredToken(actualRole, data.token);
    patchSession(actualRole, { user: data.user, winery: data.winery || null, loading: false });
    return data;
  }, [patchSession]);

  const register = useCallback(async (payload) => {
    const r = await fetch(`${API}/auth/register`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || "Registration failed");
    const actualRole = data.user.role;
    setStoredToken(actualRole, data.token);
    patchSession(actualRole, { user: data.user, winery: data.winery || null, loading: false });
    return data;
  }, [patchSession]);

  const logout = useCallback((role) => {
    clearStoredToken(role);
    patchSession(role, { user: null, winery: null, loading: false });
  }, [patchSession]);

  const refreshMe = useCallback(async (role) => {
    const t = getStoredToken(role);
    if (!t) return;
    const data = await fetchMe(t);
    if (data.user) patchSession(role, { user: data.user, winery: data.winery || null });
  }, [patchSession]);

  const setWinery = useCallback((role, winery) => patchSession(role, { winery }), [patchSession]);

  return (
    <AuthContext.Provider value={{ sessions, login, register, logout, refreshMe, setWinery }}>
      {children}
    </AuthContext.Provider>
  );
}

// useAuth(role) — every consumer must say which portal's session it wants.
// Defaults to "winery_owner" to match the pre-existing /manage/* call
// sites that called useAuth() with no argument.
export function useAuth(role = "winery_owner") {
  const ctx = useContext(AuthContext);
  if (!ctx) return null;
  const session = ctx.sessions[role] || emptySession;
  return {
    user: session.user,
    winery: session.winery,
    loading: session.loading,
    login: (email, password, r = role) => ctx.login(email, password, r),
    register: (payload) => ctx.register({ ...payload, role: payload.role || role }),
    logout: () => ctx.logout(role),
    token: () => getStoredToken(role),
    refreshMe: () => ctx.refreshMe(role),
    setWinery: (w) => ctx.setWinery(role, w),
  };
}
